import { useState } from 'react';
import { string } from 'prop-types';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { retrievePrintedGift } from 'utils/requests/gifting_requests';
import Button from '@alltrails/shared/denali/components/Button';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import * as styles from './GiftCardPrint.module.scss';
import GiftCard from '../GiftCard';
import ProgressDialog from '../../shared/ProgressDialog';

const NameAndMessage = ({ message, senderName, recipientName }) => (
  <>
    <div className={styles.senderRecipient}>
      <span>
        <FormattedMessage defaultMessage="To" />:
      </span>{' '}
      {recipientName}
    </div>
    <div className={styles.giftMessage}>{message}</div>
    <div className={styles.senderRecipient}>
      <span>
        <FormattedMessage defaultMessage="From" />:{' '}
      </span>{' '}
      {senderName}
    </div>
  </>
);

const GiftCardPrint = ({ id, code, planType, recipientName, senderName, message, background }) => {
  const languageRegionCode = useLanguageRegionCode();
  const [printingState, updatePrintingState] = useState(null);

  const handlePrintSuccess = data => {
    const downloadLink = document.createElement('a');
    downloadLink.href = data.download_url;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    updatePrintingState(null);
  };

  const printGiftCard = () => {
    updatePrintingState('loading');
    retrievePrintedGift(id, languageRegionCode)
      .then(handlePrintSuccess)
      .catch(() => updatePrintingState('error'));
  };

  const signUpText = (
    <>
      <span className={styles.bold}>
        <FormattedMessage defaultMessage="New to AllTrails?" />{' '}
      </span>
      <FormattedMessage
        defaultMessage="Go to <a></a> to create an account"
        values={{
          a: () => (
            <a href={LanguageSupportUtil.wrapUrlSafe('/signup', languageRegionCode)}>{`www.alltrails.com${LanguageSupportUtil.wrapUrlSafe(
              '/signup',
              languageRegionCode
            )}`}</a>
          )
        }}
      />
    </>
  );

  const redeemText = (
    <>
      <span className={styles.bold}>
        <FormattedMessage defaultMessage="Already have an AllTrails account?" />{' '}
      </span>
      <FormattedMessage
        defaultMessage="Use your redemption code at <a></a>"
        values={{
          a: () => (
            <a href={LanguageSupportUtil.wrapUrlSafe(`/redeem?k=${code}`, languageRegionCode)}>{`www.alltrails.com${LanguageSupportUtil.wrapUrlSafe(
              `/redeem`,
              languageRegionCode
            )}`}</a>
          )
        }}
      />
    </>
  );

  return (
    <div className={styles.giftCardPrint}>
      <div className={styles.printContainer}>
        <div className={styles.cardAndData}>
          <div className={styles.cardContainer}>
            <div className={styles.card}>
              <GiftCard backgroundId={background} classes={styles.giftCardCard} />
              <div className={styles.inMobile}>
                <NameAndMessage message={message} senderName={senderName} recipientName={recipientName} />
              </div>
            </div>
            <div className={styles.cardMeta}>
              <div className={styles.planType}>
                {planType === 'annual' ? (
                  <FormattedMessage defaultMessage="Annual Pro Plan" />
                ) : (
                  <FormattedMessage defaultMessage="Three-Year Pro Plan" />
                )}
              </div>
              <div className={styles.redemptionCode}>
                <FormattedMessage defaultMessage="Redemption code" />: <span>{code}</span>
              </div>
            </div>
          </div>
          <div className={styles.metadata}>
            <div className={styles.yourGiftCard}>
              <FormattedMessage defaultMessage="Your gift subscription" />
            </div>
            <div className={styles.desktop}>
              <NameAndMessage message={message} senderName={senderName} recipientName={recipientName} />
            </div>
          </div>
        </div>
        <div className={styles.subTextContainer}>
          <div className={styles.subText}>{signUpText}</div>
          <div className={styles.subText}>{redeemText}</div>
        </div>
      </div>
      <div className={styles.printButton}>
        <Button text={<FormattedMessage defaultMessage="Print" />} onClick={printGiftCard} testId="gift-card-print" variant="primary" />
      </div>
      {printingState && <ProgressDialog variant={printingState} updateProgressDialog={updatePrintingState} />}
    </div>
  );
};

GiftCardPrint.propTypes = {
  code: string.isRequired,
  planType: string.isRequired,
  senderName: string.isRequired,
  recipientName: string.isRequired,
  message: string.isRequired,
  background: string.isRequired
};

export default GiftCardPrint;
